export enum FormFieldType {
  INPUT = 'input',
  SELECT = 'select',
  RADIO = 'radio',
  FIELD_ARRAY = 'field_array',
  OUTCOME_FIELDS = 'outcome_fields',
  SCALE_RATING = 'scale_rating',
  OUTCOMES = 'outcomes',
  PAGE_BREAK = 'page_break',
}

export interface InputOptions {
  maxValue?: number;
  minValue?: number;
  integer?: boolean;
  maxLength?: number;
  minLength?: number;
  multiline?: boolean;
  type: 'text' | 'date' | 'email' | 'number';
}

export interface SelectOptions {
  options: string[];
  multiple?: boolean;
}

export interface RadioOptions {
  options: string[];
}

export interface ScaleRatingOptions {
  minValue: number;
  maxValue: number;
  options: string[];
  minValueLabel?: string;
  maxValueLabel?: string;
}

export interface FieldArrayOptions {
  fields: FormField[];
  layout?: 'vertical' | 'horizontal';
}

export interface OutcomesFieldOption {
  id: string;
  label: string;
  description?: string;
}

export interface OutcomesFieldCondition {
  outcomeIds: string[];
  matchingValue: string;
}

export interface OutcomesFieldOptions {
  csvContent?: string;
  conditionValue: string;
  outcomes: OutcomesFieldOption[];
  conditions: OutcomesFieldCondition[];
}

export interface OutcomesFieldValue {
  orderedIds: string[];
  outcomes: OutcomesFieldOption[];
}

export interface OutcomeFieldsOptions {
  fields: FormField[];
  sourceFieldId: string;
  maxOutcomeCount?: number;
  sourceCourseStepId: string;
}

export interface OutcomesFieldSubmissionValue {
  orderedIds: string[];
  outcomes?: OutcomesFieldOption[];
}

export type OutcomeFieldsFieldValue = Record<
  string,
  FormSubmissionData & { label?: string; description?: string }
>;

export type FormFieldTranslation = {
  label: string;
  placeholder?: string;
};

export type FormFieldTranslationWith<T> = FormFieldTranslation & T;

interface BaseFormField {
  id: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  inputId?: string | number;
}

export interface InputFormField extends BaseFormField {
  options?: InputOptions;
  type: FormFieldType.INPUT;
  translations?: Record<string, FormFieldTranslation>;
}

export interface RadioFormField extends BaseFormField {
  options?: RadioOptions;
  type: FormFieldType.RADIO;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<RadioOptions, 'options'>>
  >;
}

export interface SelectFormField extends BaseFormField {
  isSearchable?: boolean;
  options?: SelectOptions;
  type: FormFieldType.SELECT;
  translations?: Record<
    string,
    FormFieldTranslationWith<Pick<SelectOptions, 'options'>>
  >;
}

export interface ScaleRatingFormField extends BaseFormField {
  options?: ScaleRatingOptions;
  type: FormFieldType.SCALE_RATING;
  translations?: Record<
    string,
    FormFieldTranslationWith<
      Pick<ScaleRatingOptions, 'options' | 'minValueLabel' | 'maxValueLabel'>
    >
  >;
}

export interface FieldArrayFormField extends BaseFormField {
  options?: FieldArrayOptions;
  type: FormFieldType.FIELD_ARRAY;
}

export interface OutcomesFormField extends BaseFormField {
  type: FormFieldType.OUTCOMES;
  options?: OutcomesFieldOptions;
  translations?: Record<string, FormFieldTranslation>;
}

export interface OutcomeFieldsFormField extends BaseFormField {
  options?: OutcomeFieldsOptions;
  type: FormFieldType.OUTCOME_FIELDS;
}

export interface PageBreakFormField extends BaseFormField {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  options?: {};
  type: FormFieldType.PAGE_BREAK;
}

export type FormField =
  | InputFormField
  | RadioFormField
  | SelectFormField
  | OutcomesFormField
  | PageBreakFormField
  | FieldArrayFormField
  | ScaleRatingFormField
  | OutcomeFieldsFormField;

export interface FormBlock {
  id: string;
  name: string;
  fields: FormField[];
}

export interface FormData {
  blocks: FormBlock[];
}

export interface FormSubmissionData {
  [fieldId: string]:
    | string
    | number
    | string[]
    | number[]
    | OutcomesFieldValue
    | FormSubmissionData
    | OutcomeFieldsFieldValue
    | OutcomesFieldSubmissionValue;
}

export interface FormSubmission {
  id: string;
  userId: string;
  formId: string;
  createdAt: string;
  updatedAt: string;
  data: FormSubmissionData;
}

export interface ValidationError {
  fieldId: string;
  message: string;
}

export interface Form {
  id: string;
  data: FormData;
  version: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  courseStepId: string | null;
}

export interface FormSubmissionPayload {
  formId: string;
  isSubmitted: boolean;
  data: FormSubmissionData;
}
